import { defineStore } from "pinia";
import Crud from "@/store/NewsFeed/crud";
import environment from "@/environment";
import axios from "axios";
import { useRubricsStore } from "@/store/NewsFeed/rubrics";
import { useLK } from "@/controllerLK/LKstore.js";
import { useNotificationsStore } from "@/store/notifications";
import { t } from "@/controllerLK/GlobusLocalization";
import { useBlockStore } from "@/store/NewsFeed/blocks";
import { addMissingElements } from "@/functions/basic";
import { downloadTaskStatus } from "@/const/downloadTask.status";

const crud = new Crud(
  environment.appConfig.baseApiUrl + "Rubrics",
  "id",
  "",
  {}
);

const todayDate = new Date();
const sevenDayBefore =
  Date.parse(
    new Date(todayDate.setTime(todayDate.getTime() - 6 * 24 * 60 * 60 * 1000))
      .toISOString()
      .split("T")[0]
  ) / 1000;

const today = () => Date.parse(new Date()) / 1000;

const getTimestamp = () => {
  const now = new Date();
  const pad = (value) => String(value).padStart(2, "0");

  return `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}`;
};

// Название проекта может содержать HTML-разметку.
const getProjectName = () =>
  String(useLK().project?.name ?? "").replace(/<[^>]*>/g, "");

let controller;
export const useNewsFeedStore = defineStore({
  id: "newsFeed",
  state: () => ({
    ...crud.state,
    currentNews: null,
    currentPage: 0,
    offset: 0,
    take: 20,
    currentImportance: [],
    isLastNews: false,
    isMarked: false,
    dateFrom: sevenDayBefore,
    dateTo: today(),
    favoriteVisible: false,
    abort: false,
    lastSearchedValue: null,
    searchValue: "",
    isAllCardsChosen: false,
    // 0 — плашка скрыта, 1 — показана, 2 — скрыта кнопкой «Закрыть».
    selectAllBannerClosedState: 0,
    chosenCardList: [],
    chosenDetailCardList: [],
    excludedCardsIds: [],
    excludedDetailCardList: [],
    searchValueList: [],
    deleteCardList: [],
    totalCount: 1,
    pointer: false,
    facetParams: null,
    initialFacetObjects: {
      persons: [],
      locations: [],
      organisations: [],
    },
    onFilterTags: [],
    emptyNews: false,
    popupCardRubrics: false,
    popupCardTags: false,
    filterRegion: {
      title: "region",
      inputValue: "",
      list: [],
      filterList: [],
      nameList: [],
      condition: false,
    },
    filterImportance: {
      title: "importance",
      list: [],
    },
    filterSentiment: {
      title: "sentiment",
      list: [],
    },
    filterCategories: {
      title: "categories",
      list: [],
      filterList: [],
    },
    filterTags: {
      title: "tags",
      inputValue: "",
      list: [],
      listID: [],
      condition: true,
    },
    filterPoliticalAffiliation: {
      title: "political",
      inputValue: "",
      value: "not_chosen",
      condition: true,
    },
    filterObject: {
      title: "object",
      inputValue: "",
      list: [],
      listID: {
        persons: [],
        locations: [],
        organisations: [],
      },
      condition: true,
    },
    filterPersons: {
      title: "persons",
      inputValue: "",
      list: [],
      listID: [],
      condition: true,
    },
    filterOrganisations: {
      title: "organisations",
      inputValue: "",
      list: [],
      listID: [],
      condition: true,
    },

    downloadTask: {
      status: downloadTaskStatus.NO_CREATED,
      isProgress: false,
      fileName: "",
      taskId: null,
    },

    downloadOn: false,
    downloadList: [],
    projectCities: [],
    useSemSearch: false,

    notificationsStore: null,
  }),
  getters: {
    ...crud.getters,
    getFiltredItems(state) {
      if (!state.items?.length) return;
      if (state.currentImportance === 0) {
        return state.items;
      }
      return (state.items || []).filter(
        (el) => el.importance === state.currentImportance
      );
    },
  },
  actions: {
    ...crud.actions,
    async translateNewsText(title, text, id) {
      try {
        const result = await axios.post("/translator/api/v1/translate", {
          id: id.toString(),
          title: title,
          text: text,
          to_language: "ru",
        });

        this.currentNews.is_translated = true;
        return {
          title: result.data.title,
          raw_text: result.data.text,
        };
      } catch (e) {
        console.log(e);
        this.currentNews.is_translated = false;
        return text;
      }
    },
    async getNews({ preserveChosenCards = false } = {}) {
      if (this.favoriteVisible) {
        await this.getFavorite();
      } else {
        this.offset = 0;
        await this.getRubricNews({ preserveChosenCards });
      }
    },

    async getRubricNews({ preserveChosenCards = false } = {}) {
      if (!preserveChosenCards) this.resetChosenCardList();

      if (controller) {
        controller.abort();
        this.loading = true;
      }

      this.loading = true;
      this.emptyNews = false;
      this.favoriteVisible = false;
      this.currentNews = null;
      this.offset = this.currentPage * this.take;

      // Устанавливаем минимальное значение 20 переходя из проекта, где меньше 20 новостей (задача 1791)
      if (this.take < 20) this.take = 20;
      try {
        this.loading = true;
        // this.items = []
        const { data } = await this.getDataForNews();
        this.totalCount = data.total_count;
        this.take = data.total_count < 20 ? data.total_count : this.take; // Задача 1791

        this.facetParams = data;

        // Сохраняем объекты фасета для persons
        if (this.initialFacetObjects.persons.length === 0) {
          this.initialFacetObjects.persons = data.persons;
        } else if (this.initialFacetObjects.persons.length > 0) {
          addMissingElements(this.initialFacetObjects.persons, data.persons);
        }

        // Сохраняем объекты фасета для locations
        if (this.initialFacetObjects.locations.length === 0) {
          this.initialFacetObjects.locations = data.locations;
        } else if (this.initialFacetObjects.locations.length > 0) {
          addMissingElements(
            this.initialFacetObjects.locations,
            data.locations
          );
        }

        // Сохраняем объекты фасета для organisations
        if (this.initialFacetObjects.organisations.length === 0) {
          this.initialFacetObjects.organisations = data.organisations;
        } else if (this.initialFacetObjects.organisations.length > 0) {
          addMissingElements(
            this.initialFacetObjects.organisations,
            data.organisations
          );
        }

        // Сохраняем города фасета
        if (this.filterRegion.list?.length === 0) {
          this.filterRegion.list = data.cities;
        } else {
          addMissingElements(this.filterRegion.list, data.cities, "id");
        }

        this.items = data.cards;

        if (this.filterCategories.list.length === 0) {
          await this.getCategoriesList();
        }

        this.currentNews = this.items[0];
        // this.facetParams = data
        this.onFilterTags = data.tags;
        if (!this.facetParams?.cards?.length) {
          this.emptyNews = true;
          this.loading = false;
        }
        if (!this.items?.length) {
          this.emptyNews = true;
        } else {
          this.loading = false;
          this.emptyNews = false;
        }
      } catch (err) {
        this.setError(err);
      } finally {
        this.loading = false;
      }
    },

    async getCategoriesList() {
      await axios.get("/globus/api/Categories/list").then((res) => {
        this.filterCategories.list = res.data ? res.data : [];
      });
    },

    async getCities() {
      const LKstore = useLK();
      this.filterRegion.list = [];
      await axios
        .get(environment.appConfig.baseApiUrl + `Cities/${LKstore.project.id}`)
        .then((res) => {
          this.projectCities = res.data;
        });
    },

    resetChosenCardList() {
      this.isAllCardsChosen = false;
      this.chosenCardList = [];
      this.chosenDetailCardList = [];
      this.excludedCardsIds = [];
      this.excludedDetailCardList = [];
      this.selectAllBannerClosedState = 0;
    },

    clearNewsFeed() {
      this.currentPage = 0;
      this.totalCount = 0;
      this.isLastNews = false;
      this.items = [];
      this.currentNews = null;
      this.offset = 0;
    },

    clearFacet() {
      this.filterTags.condition = true;
      this.filterObject.condition = true;
      this.filterRegion.condition = true;
      this.filterRegion.list = [];
      this.filterRegion.inputValue = "";
      this.filterRegion.filterList = [];

      this.filterImportance.list = [];

      this.filterSentiment.list = [];

      this.filterCategories.list = [];
      this.filterCategories.filterList = [];

      this.filterTags.list = [];
      this.filterTags.listID = [];
      this.filterTags.inputValue = "";

      this.filterObject.list = [];
      this.filterObject.listID.persons = [];
      this.filterObject.listID.locations = [];
      this.filterObject.listID.organisations = [];
      this.filterObject.inputValue = "";

      this.dateFrom = sevenDayBefore;
      this.dateTo = today();
      this.facetParams = null;
      this.initialFacetObjects = {
        persons: [],
        locations: [],
        organisations: [],
      };
    },

    async updateFacet() {
      await this.getDataForNews().then((res) => {
        this.facetParams = res.data;
      });
    },

    async getDataForNews() {
      controller = new AbortController();
      const blockStore = useBlockStore();
      const rubricsStore = useRubricsStore();
      const rubricIDs = rubricsStore.chosenRubrics;
      const themesIds = blockStore.chosenThemes;
      const mainURL = "/globus/api/cards";
      const url = `${mainURL}?offset=${this.offset}&take=${this.take}`;

      return await axios.post(
        url,
        {
          q: this.searchValue,
          project_id: useLK().project.id,
          rubrics: rubricIDs,
          blocks: themesIds,
          categories: this.filterCategories.filterList,
          cities: this.filterRegion.filterList,
          and_for_cities: this.filterRegion.condition,
          importance: this.filterImportance.list,
          sentiments: this.filterSentiment.list,
          tags: this.filterTags.listID,
          and_for_tags: this.filterTags.condition,
          and_for_objects: this.filterObject.condition,
          date_from: this.dateFrom,
          date_to: this.dateTo,
          persons: this.filterObject.listID.persons,
          locations: this.filterObject.listID.locations,
          organisations: this.filterObject.listID.organisations,
          use_sem_search: this.useSemSearch,
          political_affiliation: this.filterPoliticalAffiliation.value,
        },
        {
          signal: controller.signal,
        }
      );
    },

    // Добавление новости в избранное
    async setFavorite(cardId, is_favourite, isMassAction = false) {
      const url = `/globus/api/cards/${cardId}`;
      return await axios
        .patch(
          url,
          { is_favourite },
          {
            headers: {
              "Content-Type": "application/merge-patch+json",
            },
          }
        )
        .then(() => {
          this.items.forEach((item) => {
            if (item.id === cardId) {
              item.is_favourite = is_favourite;
            }
          });

          if (isMassAction) this.selectAllBannerClosedState = 0;
        })
        .catch((err) => {
          this.setError(err);
        });
    },

    async getFavorite() {
      if (controller) {
        controller.abort();
        this.loading = true;
      }

      controller = new AbortController();
      this.items = [];
      this.loading = true;
      this.currentNews = null;

      this.offset = this.currentPage * this.take;
      const LKstore = useLK();
      const url = `/globus/api/cards/favorite?offset=${this.offset}&take=${
        this.take > 0 ? this.take : 20
      }`;
      return axios
        .post(
          url,
          {
            q: this.searchValue,
            project_id: LKstore.project.id,
            categories: this.filterCategories.filterList,
            cities: this.filterRegion.filterList,
            and_for_cities: this.filterRegion.condition,
            importance: this.filterImportance.list,
            sentiments: this.filterSentiment.list,
            tags: this.filterTags.listID,
            and_for_tags: this.filterTags.condition,
            date_from: this.dateFrom,
            date_to: this.dateTo,
            persons: this.filterObject.listID.persons,
            locations: this.filterObject.listID.locations,
            organisations: this.filterObject.listID.organisations,
            use_sem_search: this.useSemSearch,
          },
          {
            signal: controller.signal,
          }
        )
        .then((res) => {
          this.totalCount = res.data.total_count;
          this.items = res.data.cards;
          this.currentNews = this.items[0];
          this.loading = false;
          if (!res.data.cards.length) {
            this.emptyNews = true;
          }
        })
        .catch((err) => {
          this.setError(err);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    // Обновляет города в карточке.
    async setCityCard(cardId, cityList) {
      let url = `/globus/api/cards/${cardId}/cities`;
      try {
        await axios.patch(url, cityList);
      } catch (error) {
        console.log(error);
      }
    },

    async facetSearch(string) {
      if (this.searchValue) {
        await this.getRubricNews();

        const escapedString = string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        const regex = new RegExp(escapedString, "gi");

        this.items.forEach((item) => {
          item.text = item.text.replace(
            regex,
            `<span class='marked-word'>${string}</span>`
          );
          item.raw_text = item.raw_text.replace(
            regex,
            `<span class='marked-word'>${string}</span>`
          );
          item.title = item.title.replace(
            regex,
            `<span class='marked-word'>${string}</span>`
          );
        });
      } else {
        await this.getRubricNews();
      }
    },

    async deleteCard(cardId) {
      const LKStore = useLK();

      try {
        const currentCard = this.items.find((item) => item.id === cardId);
        if (currentCard) {
          this.items.splice(this.items.indexOf(currentCard), 1);
        }
        await axios.delete(
          `/globus/api/projects/${LKStore.project.id}/cards/${cardId}`
        );
        this.selectAllBannerClosedState = 0;
      } catch (e) {
        console.log(e);
      }
    },

    // Изменение сантимента новости
    async changeSentiment(cardId, sentiment) {
      const url = `/globus/api/cards/${cardId}`;
      try {
        await axios.patch(
          url,
          { sentiment },
          {
            headers: {
              "Content-Type": "application/merge-patch+json",
            },
          }
        );
      } catch (e) {
        console.log(e);
      }
    },

    // Изменение значимости новости.
    async changeImportance(cardId, importance) {
      const url = `/globus/api/cards/${cardId}`;
      try {
        await axios.patch(
          url,
          { importance },
          {
            headers: {
              "Content-Type": "application/merge-patch+json",
            },
          }
        );
      } catch (e) {
        console.log(e);
      }
    },

    // Обновление рубрик карточки
    async updateCardRubrics(cardId, rubricsIds) {
      try {
        this.notificationsStore = useNotificationsStore();
        await axios.patch(`/globus/api/cards/${cardId}/rubrics/`, rubricsIds);
        this.currentNews.rubric_ids = rubricsIds;
        this.notificationsStore.createNotify(
          t("toast.changes_saved"),
          "success"
        );
      } catch (e) {
        console.log(e);
      }
    },

    // Обновление тегов карточки
    async updateCardTags(cardId, tagsIds) {
      try {
        this.notificationsStore = useNotificationsStore();
        await axios.patch(`/globus/api/cards/${cardId}/tags`, tagsIds);
        this.currentNews.source_tag_ids = tagsIds;
        this.notificationsStore.createNotify(
          t("toast.changes_saved"),
          "success"
        );
      } catch (e) {
        console.log(e);
      }
    },

    // Обновление категории карточек
    async updateCardCategory(cardId, categoryId) {
      try {
        this.notificationsStore = useNotificationsStore();
        await axios.patch(`/globus/api/cards/${cardId}/category/${categoryId}`);
        this.currentNews.category_id = categoryId;
        this.notificationsStore.createNotify(
          t("toast.changes_saved"),
          "success"
        );
      } catch (e) {
        console.log(e);
      }
    },

    // Запуск цепочки действий по загрузке файла (проверка/создание/скачивание)
    async filesDownload(
      cards_ids = [],
      external_ids = [],
      sourceType = "selected",
      searchRequest = { mode: "by_rubric", additionalProp1: {} },
      excluded_cards_ids = [],
      excluded_external_ids = [],
      chosenCount = 0
    ) {
      this.notificationsStore = useNotificationsStore();
      this.downloadTask.isProgress = true;

      try {
        await this.checkingExistedDownloadTask();

        if (this.downloadTask.status === downloadTaskStatus.NO_CREATED) {
          await this.createDownloadTask(
            cards_ids,
            external_ids,
            sourceType,
            searchRequest,
            excluded_cards_ids,
            excluded_external_ids,
            chosenCount
          );
        }

        if (
          this.downloadTask.status === downloadTaskStatus.CREATED ||
          this.downloadTask.status === downloadTaskStatus.PROCESSING
        ) {
          await this.checkingExistedDownloadTask();
        }

        if (this.downloadTask.status === downloadTaskStatus.COMPLETED) {
          await this.downloadFile();
          this.downloadTask.taskId = null;
          this.selectAllBannerClosedState = 0;
        }

        if (
          this.downloadTask.status === downloadTaskStatus.FAILED ||
          this.downloadTask.status === downloadTaskStatus.CANCELLED
        ) {
          this.downloadTask.taskId = null;
        }
      } finally {
        this.downloadTask.isProgress = false;
      }
    },

    // Проверка статуса задачи на загрузку файла (в интервале)
    async checkingExistedDownloadTask() {
      const taskId = this.downloadTask.taskId;
      if (!taskId) {
        this.downloadTask.status = downloadTaskStatus.NO_CREATED;
        return;
      }

      this.downloadTask.status = downloadTaskStatus.CREATED;

      await new Promise((resolve, reject) => {
        let inFlight = false;

        const interval = setInterval(async () => {
          if (inFlight) return;
          inFlight = true;

          try {
            const isCompleted = await this.checkDownloadTask(taskId);
            const status = this.downloadTask.status;
            const isFinished =
              isCompleted ||
              status === downloadTaskStatus.FAILED ||
              status === downloadTaskStatus.CANCELLED;

            if (isFinished) {
              clearInterval(interval);
              if (status === downloadTaskStatus.FAILED) {
                this.notificationsStore.createNotify(
                  t("toast.download_task_failed"),
                  "error"
                );
              }
              resolve();
            }
          } catch (error) {
            clearInterval(interval);
            reject(error);
          } finally {
            inFlight = false;
          }
        }, 2000);
      });
    },

    // Проверка статуса задачи на загрузку файла
    async checkDownloadTask(taskId) {
      try {
        const response = await axios.get(`/globus/api/offloads/${taskId}`);

        this.downloadTask.status =
          response.data?.status || downloadTaskStatus.FAILED;
        return this.downloadTask.status === downloadTaskStatus.COMPLETED;
      } catch (error) {
        console.log(error);
        this.downloadTask.status = downloadTaskStatus.FAILED;
        return false;
      }
    },

    // Создание задачи на загрузку файла
    async createDownloadTask(
      cards_ids,
      external_ids,
      source_type,
      search_request,
      excluded_cards_ids = [],
      excluded_external_ids = [],
      chosenCount
    ) {
      const projectName = getProjectName().toLowerCase().replaceAll(" ", "");
      const taskRec = {
        source_type,
        cards_ids,
        external_ids,
        search_request,
        format: "xlsx",
        template_code: "default_cards_export",
        file_name: `${projectName}-${getTimestamp()}.xlsx`,
      };
      if (external_ids?.length) {
        const rubricsStore = useRubricsStore();
        taskRec.rubric_id =
          rubricsStore.chosenRubrics[0] ?? rubricsStore.currentRubric?.id;
      }

      if (excluded_cards_ids?.length) {
        taskRec.excluded_cards_ids = excluded_cards_ids;
      }

      if (excluded_external_ids?.length) {
        taskRec.excluded_external_ids = excluded_external_ids;
      }

      this.downloadTask.fileName = taskRec.file_name;

      const response = await axios.post("/globus/api/offloads", taskRec);

      if (response.data?.status === downloadTaskStatus.CREATED) {
        this.notificationsStore.createNotify(
          t("toast.create_task_success"),
          "success"
        );

        if (chosenCount >= 5000) {
          this.notificationsStore.createNotify(
            this.getTaskMessageStarted5000(),
            "success"
          );
        }

        this.downloadTask.status = response.data.status;
        this.downloadTask.taskId = response.data?.task_id || null;
        return;
      } else {
        this.downloadTask.status =
          response.data?.status || downloadTaskStatus.FAILED;
        this.notificationsStore.createNotify(
          t("toast.create_task_failed"),
          "error"
        );
      }
    },

    getTaskMessageStarted5000() {
      return t("toast.download_task_started_5000").replaceAll(
        "{reportName}",
        getProjectName()
      );
    },

    // Скачивание файла
    async downloadFile() {
      const response = await axios.get(
        `/globus/api/offloads/${this.downloadTask.taskId}/download`,
        { responseType: "blob" }
      );

      if (response.status !== 200) {
        this.downloadTask.status = downloadTaskStatus.FAILED;
        this.notificationsStore.createNotify(
          t("toast.download_task_failed"),
          "error"
        );
        return;
      }

      this.downloadTask.status = downloadTaskStatus.COMPLETED;
      this.notificationsStore.createNotify(
        t("toast.download_task_completed"),
        "success"
      );

      const fileUrl = window.URL.createObjectURL(response.data);
      const a = document.createElement("a");
      a.href = fileUrl;
      a.download = this.downloadTask.fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(fileUrl);
    },

    // Добавление/Удаление объекта в список фасета.
    // true - Добавлен. false - Удален.
    async toggleObjectInFilter(objectId, objectType) {
      // Проверяем, есть ли уже этот объект в фильтре
      if (this.filterObject.list.some((obj) => obj === objectId)) {
        // Если есть, удаляем его
        this.filterObject.list = this.filterObject.list.filter(
          (obj) => obj !== objectId
        );
        this.filterObject.listID[objectType] = this.filterObject.listID[
          objectType
        ].filter((id) => id !== objectId);
        return false;
      } else {
        // Если нет, добавляем
        this.filterObject.list.push(objectId);
        this.filterObject.listID[objectType].push(objectId);
        return true;
      }
    },
  },
});
//
